/**
 * Core-100 R5 (CORE-C F/Prov, CORE-E §Provenance) — which evidence class ONE category proficiency value carries.
 *
 * CORE-E: "A manually-entered 70% and an evidence-derived 70% are different claims and must not render identically."
 * CORE-C l.41: assertion, attestation and derivation are "distinguishable on sight".
 *
 * Two readers, one rule:
 *   - the PORTABLE record (`technician_proficiencies.provenance_class`) is classed at rest by its single writer,
 *     `__project_portable_proficiency` (20270206011900);
 *   - the LIVE shop value (`technicians.proficiencies`) carries no class itself, so it is classed from CORE-E's
 *     authority row with the SAME rule the portable writer applies (`classifyLiveProficiency` mirrors its CASE).
 *
 * A class that cannot be read (the authority read failed) is NULL — the section then states nothing about the class,
 * which is different from `unrecorded` ("its source was not recorded"), a fact the record itself holds.
 *
 * The words come from the heatmap's existing per-class vocabulary (`PORTABLE_CLASS_LABEL` / `PORTABLE_CLASS_MARK`),
 * so one fact reads the same in both places; nothing new is invented here.
 */
import {
  portableProvenanceClassOf,
  PORTABLE_CLASS_LABEL,
  PORTABLE_CLASS_MARK,
  type PortableProvenanceClass,
} from './skillHeatmap/types';

export { PORTABLE_CLASS_LABEL, PORTABLE_CLASS_MARK, portableProvenanceClassOf };
export type { PortableProvenanceClass };

/** The `PASSPORT_FACT_PROVENANCE` group a class selects, or NULL when the class is unknown / unrecorded. */
export function proficiencyFactGroup(cls: PortableProvenanceClass | null | undefined): string | null {
  switch (cls) {
    case 'demonstrated':
      return 'proficiency-demonstrated';
    case 'manager_asserted':
      return 'proficiency-manager-baseline';
    case 'passport_carried':
      return 'proficiency-passport-carried';
    default:
      return null;
  }
}

export interface AuthorityClassInput {
  demonstrated_evidence_count?: number | null;
  baseline_class?: string | null;
}

/**
 * Mirror of the portable writer's CASE (`__project_portable_proficiency`, 20270206011900):
 *   demonstrated evidence > 0 → demonstrated; else coalesce(authority.baseline_class, technicians.proficiency_baseline_class)
 *   MANAGER_ASSERTED → manager_asserted, PASSPORT_CARRIED → passport_carried, anything else → unrecorded.
 */
export function classifyLiveProficiency(
  authority: AuthorityClassInput | null | undefined,
  technicianBaselineClass: string | null | undefined,
): PortableProvenanceClass {
  if ((authority?.demonstrated_evidence_count ?? 0) > 0) return 'demonstrated';
  const baseline = authority?.baseline_class ?? technicianBaselineClass ?? null;
  if (baseline === 'MANAGER_ASSERTED') return 'manager_asserted';
  if (baseline === 'PASSPORT_CARRIED') return 'passport_carried';
  return 'unrecorded';
}

/** The narrow client surface this file needs; the real SupabaseClient is passed whole (never a detached method). */
interface AuthorityReader {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (col: string, v: string) => {
        eq: (col: string, v: string) => PromiseLike<{ data: unknown; error: unknown }>;
      };
    };
  };
}

/**
 * Per-category classes for a LIVE technicians row, read from CORE-E's authority (shop-member RLS) and the row's own
 * `proficiency_baseline_class` (the writer's fallback when no authority row exists).
 * Returns NULL when either read fails, so the caller shows no class rather than a wrong one.
 */
export async function fetchLiveProficiencyClasses(
  client: unknown,
  technicianId: string,
  shopId: string,
  categoryKeys: readonly string[],
): Promise<Record<string, PortableProvenanceClass> | null> {
  try {
    // `client` is the whole SupabaseClient; each call goes through it (a detached method loses its receiver).
    const reader = client as AuthorityReader;
    const [{ data, error }, tech] = await Promise.all([
      reader
        .from('technician_category_authority')
        .select('category_key, demonstrated_evidence_count, baseline_class')
        .eq('technician_id', technicianId)
        .eq('shop_id', shopId),
      reader
        .from('technicians')
        .select('proficiency_baseline_class')
        .eq('id', technicianId)
        .eq('shop_id', shopId),
    ]);
    if (error || tech.error) return null;
    const techRow = ((tech.data ?? []) as Array<Record<string, unknown>>)[0];
    const technicianBaselineClass = (techRow?.proficiency_baseline_class as string | null | undefined) ?? null;
    const byCategory = new Map<string, AuthorityClassInput>();
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      byCategory.set(String(row.category_key), {
        demonstrated_evidence_count:
          row.demonstrated_evidence_count === null || row.demonstrated_evidence_count === undefined
            ? null
            : Number(row.demonstrated_evidence_count),
        baseline_class: (row.baseline_class as string | null | undefined) ?? null,
      });
    }
    const out: Record<string, PortableProvenanceClass> = {};
    for (const key of categoryKeys) out[key] = classifyLiveProficiency(byCategory.get(key), technicianBaselineClass);
    return out;
  } catch {
    return null;
  }
}
